import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { getCustomerSubscriptionHistoryApi } from 'api';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import { useStyles } from '../styles';
import HistoryTableToolbar from './HistoryTableToolbar';
import HistoryRowCollapsible from './HistoryRowCollapsible';
import HistoryFilterDrawer from './HistoryFilterDrawer';


const defaultTableCells = [
  {
    label: 'Date',
    sortField: 'created_at',
    display: true,
  },
  {
    label: 'Subscription Status',
    display: true,
  },
  {
    label: 'Order Id',
    sortField: 'order_id',
    display: true,
  },
  {
    label: 'Changed By',
    display: true,
  },
  {
    label: 'Description',
    display: true,
  },
];

const HistoryList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {id} = useParams<{ id: string }>();
  const {customerSubscriptionHistory, customerSubscriptionHistoryTotalCount } = useSelector((state: IReduxState) => state.customer_subscriptions);
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>({});
  const [searchConditions, setSearchConditions] = useState({});
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('SubscriptionHistoryTableCells', defaultTableCells);

  useEffect(() => {
    getCustomerSubscriptionHistoryApi({
      dispatch,
      id,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortField,
    sortDirection,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-8">
      <div className={`p-5 ${classes.toolbarContainer}`}>
        <HistoryTableToolbar
          onSearch={onSearch}
          setOpenFilter={setOpenFilter}
          filterConditions={filterConditions}
          tableCells={headTableCells}
          setHeadTableCells={setHeadTableCells}
        />
      </div>
      <div className="table-container">
        <TableContainer>
          <Table aria-label="collapsible table" className={classes.table}>
            <TableHead>
              <TableRow>
                {headTableCells.map((cell, index) => {
                  if (!cell.display) {
                    return null;
                  }

                  return (
                    <TableCell
                      key={index}
                      className="min-w-80"
                    >
                      {cell.sortField ? (
                        <TableSortLabel
                          active={sortField === cell.sortField}
                          IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                          onClick={(event) => handleRequestSort(event, cell.sortField)}
                        >
                          {cell.label}
                        </TableSortLabel>
                      ) : (
                        cell.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!customerSubscriptionHistoryTotalCount ?
                <TableRow>
                  <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                    No matching orders found.
                  </TableCell>
                </TableRow>
                :
                customerSubscriptionHistory.map((el) => (
                  <HistoryRowCollapsible
                    key={el.increment_id}
                    customerSubscriptionHistoryData={el}
                    headTableCells={headTableCells}
                  />
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        {!!customerSubscriptionHistoryTotalCount &&
        <TablePagination
          rowsPerPageOptions={[10, 20, 40, 80, 100]}
          component="div"
          className={classes.paginationContainer}
          count={customerSubscriptionHistoryTotalCount}
          rowsPerPage={rowsPerPage}
          page={currentPage - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        }
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <HistoryFilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
    </div>
  );
};

export default HistoryList;
